import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { escalatePayable, getMyDeliveryPayables, remindOwnerPayable } from '../api';

// delivery_person_payables.md - the delivery person's receivables from business owners. This is
// the first place a delivery-role account sees money (MyBookings §9 keeps every other view
// money-free) - and it's strictly their OWN money: what an owner owes them per completed leg.
const STATUS_VARIANT = { pending: 'warning', escalated: 'info', paid: 'success', rejected: 'secondary' };
const LEG_LABEL = { OUTBOUND: 'Delivery', RETURN: 'Return', VISIT: 'Visit' };
const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function DeliveryPayablesPanel() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [escalateFor, setEscalateFor] = useState(null);
  const [escalateNote, setEscalateNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMyDeliveryPayables(statusFilter === 'all' ? undefined : statusFilter);
      setRows(res.data);
      setSummary(res.summaryByOwner);
    } catch {
      setError('Could not load your payments.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRemind = async (id) => {
    setBusyId(id);
    try { await remindOwnerPayable(id); await load(); } finally { setBusyId(null); }
  };

  const submitEscalate = async () => {
    setBusyId(escalateFor);
    try {
      await escalatePayable(escalateFor, escalateNote.trim());
      setEscalateFor(null);
      setEscalateNote('');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const rowsPaged = usePagedArray(rows, { resetKey: statusFilter });

  if (loading) return <Spinner animation="border" role="status" />;

  const totalOutstanding = summary.reduce((s, o) => s + (o.outstanding || 0), 0);

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}

      {summary.length > 0 && (
        <div className="row g-2 mb-3">
          <div className="col-12">
            <Alert variant="light" className="fw-semibold mb-2">Outstanding across all owners: {rupees(totalOutstanding)}</Alert>
          </div>
          {summary.filter((o) => o.outstanding > 0).map((o) => (
            <div className="col-md-4" key={o.ownerId}>
              <Card className="h-100">
                <Card.Body className="py-2">
                  <div className="fw-semibold">{o.ownerName || 'Owner'}</div>
                  <div className="small">Owed: <strong>{rupees(o.outstanding)}</strong></div>
                  {o.escalated > 0 && <div className="small text-info">{rupees(o.escalated)} escalated</div>}
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      <Form.Select
        size="sm" style={{ maxWidth: 180 }} className="mb-3"
        value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
        aria-label="Payment status filter"
      >
        <option value="all">All</option>
        <option value="pending">Pending</option>
        <option value="escalated">Escalated</option>
        <option value="paid">Paid</option>
        <option value="rejected">Rejected</option>
      </Form.Select>

      {rowsPaged.total === 0 ? (
        <Alert variant="light">Nothing here.</Alert>
      ) : (
        <>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Order</th>
              <th>Owner</th>
              <th>For</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rowsPaged.rows.map((p) => (
              <tr key={p._id}>
                <td className="font-monospace">#{p.bookingRef}</td>
                <td>
                  {p.ownerName || '—'}
                  {p.ownerPhone && <div className="small text-muted">{p.ownerPhone}</div>}
                </td>
                <td>{LEG_LABEL[p.leg] || p.leg}</td>
                <td>{rupees(p.amount)}</td>
                <td>
                  <Badge bg={STATUS_VARIANT[p.status] || 'secondary'}>{p.status}</Badge>
                  {p.reminders?.length > 0 && p.status !== 'paid' && (
                    <div className="small text-muted">{p.reminders.length} reminder{p.reminders.length > 1 ? 's' : ''} sent</div>
                  )}
                </td>
                <td className="text-nowrap">
                  {['pending', 'escalated'].includes(p.status) && (
                    <Button size="sm" variant="outline-secondary" className="me-1" disabled={busyId === p._id} onClick={() => handleRemind(p._id)}>
                      Remind
                    </Button>
                  )}
                  {p.status === 'pending' && (
                    <Button size="sm" variant="outline-warning" disabled={busyId === p._id} onClick={() => { setEscalateFor(p._id); setEscalateNote(''); }}>
                      Escalate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...rowsPaged} />
        </>
      )}

      {escalateFor && (
        <Card className="mt-2 border-warning">
          <Card.Body>
            <div className="fw-semibold mb-1">Escalate this unpaid fee to the website owner?</div>
            <p className="small text-muted mb-2">The website owner / finance team will review it and follow up with the business owner.</p>
            <Form.Control
              as="textarea" rows={2} size="sm" className="mb-2" maxLength={1000}
              placeholder="Anything they should know (optional)"
              value={escalateNote} onChange={(e) => setEscalateNote(e.target.value)}
            />
            <Button size="sm" variant="warning" className="me-2" disabled={busyId === escalateFor} onClick={submitEscalate}>Escalate</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setEscalateFor(null)}>Cancel</Button>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
